import React from "react";
import injectSheet from "react-jss";
import { DropTarget } from "react-dnd";
import { v4 as uuid } from "uuid";
import FloorplanHotspot from "./FloorplanHotspot";

class FloorplanEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      hoveredUuid: null,
    };

    this.image = React.createRef();

    this.onUpdateHotspot = this.onUpdateHotspot.bind(this);
    this.onDeleteHotspot = this.onDeleteHotspot.bind(this);
    this.setHeight = this.setHeight.bind(this);
  }

  componentDidMount() {
    window.addEventListener("resize", this.setHeight);
    if (this.image.current?.complete) {
      this.setState({ loaded: true });
      this.setHeight();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.setHeight);
  }

  getXYFromMouse(x, y) {
    const rect = this.getImageRect();

    let normX = (x - rect.x) / rect.width;
    let normY = (y - rect.y) / rect.height;

    return {
      x: normX,
      y: normY,
    };
  }

  setHeight() {
    const rect = this.getImageRect();
    this.setState({ imgWidth: rect.width, imgHeight: rect.height });
    this.props.setFloorplanHeight(rect.height);
  }

  getImageRect() {
    if (this.image.current) {
      return this.image.current.getBoundingClientRect();
    }

    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
  }

  onUpdateHotspot(hotspot) {
    let { hotspots } = this.props.floorplan;
    let updated = false;

    hotspots = hotspots.map(item => {
      if (item.uuid !== hotspot.uuid) {
        return item;
      }

      updated = true;
      return hotspot;
    });

    if (updated === false) {
      hotspots.push(hotspot);
    }

    this.props.onUpdateFloorplan(
      Object.assign({}, this.props.floorplan, {
        hotspots,
      }),
    );
  }

  onDeleteHotspot(hotspot) {
    const { hotspots } = this.props.floorplan;

    this.props.onUpdateFloorplan(
      Object.assign({}, this.props.floorplan, {
        hotspots: hotspots.filter(item => item.uuid !== hotspot.uuid),
      }),
    );
  }

  getLocation(hotspot) {
    const rect = this.getImageRect();

    return {
      left: hotspot.x * rect.width,
      top: hotspot.y * rect.height,
    };
  }

  render() {
    const { classes, floorplan, connectDropTarget, images, medias } = this.props;

    const { loaded, hoveredUuid } = this.state;

    return connectDropTarget(
      <div className={classes.container}>
        <img
          ref={this.image}
          src={floorplan.url.replace(
            "dev.movein360.com",
            "movein.theatro360.com",
          )}
          className={classes.image}
          alt={floorplan.name}
          onLoad={() => {
            this.setState({ loaded: true });
            this.setHeight();
          }}
        />
        {loaded &&
          floorplan.hotspots.map(item => (
            <FloorplanHotspot
              key={item.uuid}
              hotspot={item}
              style={this.getLocation(item)}
              images={images}
              medias={medias}
              onDeleteHotspot={this.onDeleteHotspot}
              hovered={hoveredUuid === item.uuid}
              onHover={() => this.setState({ hoveredUuid: item.uuid })}
              onUnhover={() =>
                this.setState(state =>
                  state.hoveredUuid === item.uuid
                    ? { hoveredUuid: null }
                    : null,
                )
              }
            />
          ))}
      </div>,
    );
  }
}

const styles = theme => ({
  container: {
    margin: [15, 30],
    position: "relative",
    width: "calc(100% - 60px)",
    height: "calc(100% - 30px)",
    "box-sizing": "border-box",
  },
  image: {
    display: "block",
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "100%",
    "object-fit": "contain",
  },
});

const imageTarget = {
  drop(props, monitor, component) {
    const result = monitor.getClientOffset();
    const item = monitor.getItem();

    const id = item.hotspot !== undefined ? item.hotspot.uuid : (
      item.uuid || uuid()
    );
    const { x, y } = component.getXYFromMouse(result.x, result.y);
    const hotspot = {
      uuid: id,
      image: item.id,
      type: item.type,
      x,
      y,
    };
    component.onUpdateHotspot(hotspot);
  },
};

export default DropTarget(
  ["floorplan-image", "floorplan-hotspot-draggable", "floorplan-media"],
  imageTarget,
  connect => ({
    connectDropTarget: connect.dropTarget(),
  }),
)(injectSheet(styles)(FloorplanEditor));
